/**
 * DefinitionCard
 *
 * A component for displaying interactive term definitions in chat messages
 *
 * Primary responsibilities:
 * - Display a term and its definition
 * - Allow expanding/collapsing of examples
 * - Show related terms when available
 * - Support compact and expanded views
 */

import React, { useState } from 'react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { IconBook, IconChevronDown, IconChevronRight, IconExternalLink } from '@tabler/icons-react'

interface DefinitionCardProps {
  term: string
  definition: string
  examples?: string[]
  relatedTerms?: Array<{
    term: string
    link?: string
  }>
  className?: string
}

export const DefinitionCard: React.FC<DefinitionCardProps> = ({
  term,
  definition,
  examples = [],
  relatedTerms = [],
  className
}) => {
  const [showExamples, setShowExamples] = useState(false)

  const toggleExamples = () => {
    setShowExamples(!showExamples)
  }

  return (
    <div
      className={cn(
        'border-border bg-card my-4 flex flex-col overflow-hidden rounded-lg border shadow-sm transition-all',
        className
      )}
    >
      <div className="flex items-center border-b p-3">
        <div className="flex items-center gap-2">
          <IconBook size={16} className="text-muted-foreground" />
          <h3 className="text-sm font-medium">Definition</h3>
        </div>
      </div>

      <div className="p-4">
        <h4 className="mb-2 text-lg font-medium">{term}</h4>
        <p className="text-muted-foreground mb-4 text-sm">{definition}</p>

        {examples.length > 0 && (
          <div className="mt-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleExamples}
              className="mb-2 flex items-center gap-1 text-xs"
            >
              {showExamples ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
              {examples.length} Example{examples.length !== 1 ? 's' : ''}
            </Button>

            {showExamples && (
              <div className="space-y-2 border-l-2 border-blue-200 pl-4 dark:border-blue-800">
                {examples.map((example, index) => (
                  <div key={index} className="text-sm">
                    <p className="text-muted-foreground">{example}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {relatedTerms.length > 0 && (
          <div className="mt-4 border-t pt-3">
            <h5 className="mb-2 text-xs font-medium">Related Terms</h5>
            <div className="flex flex-wrap gap-2">
              {relatedTerms.map((related, index) =>
                related.link ? (
                  <a
                    key={index}
                    href={related.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 rounded-full bg-blue-100 px-2 py-1 text-xs text-blue-800 transition-colors hover:bg-blue-200 dark:bg-blue-900/20 dark:text-blue-300 dark:hover:bg-blue-800/30"
                  >
                    {related.term}
                    <IconExternalLink size={10} />
                  </a>
                ) : (
                  <span
                    key={index}
                    className="rounded-full bg-blue-100 px-2 py-1 text-xs text-blue-800 dark:bg-blue-900/20 dark:text-blue-300"
                  >
                    {related.term}
                  </span>
                )
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
